import { useEffect, useState } from "react";
import api from "@/lib/api";
import { money, dt } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { ProductPicker } from "@/components/ProductPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tag, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const empty = { nombre: "", tipo: "porcentaje", valor: 0, aplica_a: "todos", categorias: [], productos_ids: [], marcas: [], activa: true, texto_para_el_agente: "" };
const ALCANCE_LABELS = { todos: "Toda la tienda", productos: "Productos específicos", marca: "Marca", categoria: "Categoría" };

export default function Promotions() {
  const [promos, setPromos] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [marcasDisponibles, setMarcasDisponibles] = useState([]);
  const [categoriasDisponibles, setCategoriasDisponibles] = useState([]);
  const [productosNombres, setProductosNombres] = useState({});
  const [preview, setPreview] = useState(null);
  const [verCuales, setVerCuales] = useState(false);

  const load = () => api.get("/promotions").then((r) => setPromos(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/products/marcas").then((r) => setMarcasDisponibles(r.data)); }, []);
  useEffect(() => { api.get("/categorias").then((r) => setCategoriasDisponibles(r.data.filter(Boolean))); }, []);

  useEffect(() => {
    if (!editing) return;
    const t = setTimeout(() => {
      api.get("/promotions/preview-alcance", { params: {
        aplica_a: form.aplica_a,
        productos_ids: (form.productos_ids || []).join(","),
        marcas: (form.marcas || []).join(","),
        categorias: (form.categorias || []).join(","),
      } }).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(t);
  }, [editing, form.aplica_a, form.productos_ids, form.marcas, form.categorias]);

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("Poné un nombre");
    try {
      const payload = { ...form, valor: Number(form.valor) };
      if (editing === "new") await api.post("/promotions", payload);
      else await api.put(`/promotions/${editing}`, payload);
      toast.success("Promoción guardada"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const toggle = async (p) => { await api.post(`/promotions/${p.id}/toggle`); load(); };
  const del = async (p) => { await api.delete(`/promotions/${p.id}`); load(); toast.success("Eliminada"); };

  const toggleMarca = (m) => setForm((f) => ({ ...f, marcas: f.marcas.includes(m) ? f.marcas.filter((x) => x !== m) : [...f.marcas, m] }));
  const toggleCategoria = (c) => setForm((f) => ({ ...f, categorias: f.categorias.includes(c) ? f.categorias.filter((x) => x !== c) : [...f.categorias, c] }));
  const addProducto = (pid) => {
    if (!pid || form.productos_ids.includes(pid)) return;
    setForm((f) => ({ ...f, productos_ids: [...f.productos_ids, pid] }));
    api.get(`/products/${pid}`).then((r) => setProductosNombres((n) => ({ ...n, [pid]: r.data.nombre })));
  };
  const removeProducto = (pid) => setForm((f) => ({ ...f, productos_ids: f.productos_ids.filter((x) => x !== pid) }));

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      <PageHeader title="Promociones" subtitle="El agente solo ofrece promociones activas.">
        <Button onClick={() => { setForm(empty); setEditing("new"); }} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="new-promo"><Plus className="h-4 w-4" /> Nueva promo</Button>
      </PageHeader>

      {!promos ? <Loader /> : promos.length === 0 ? (
        <EmptyState icon={Tag} title="Sin promociones" subtitle="Creá tu primera promoción para que el agente la ofrezca." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {promos.map((p) => (
            <div key={p.id} data-testid={`promo-${p.id}`} className={`ms-card p-5 ${p.activa ? "" : "opacity-70"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-head font-semibold">{p.nombre}</h3>
                  <p className="text-primary font-semibold mt-1">{p.tipo === "porcentaje" ? `${p.valor}%` : money(p.valor)} · <span className="text-muted-foreground font-normal">{ALCANCE_LABELS[p.aplica_a] || p.aplica_a}</span></p>
                  {p.aplica_a === "marca" && p.marcas?.length > 0 && <p className="text-xs text-muted-foreground">{p.marcas.join(", ")}</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Switch checked={p.activa} onCheckedChange={() => toggle(p)} data-testid={`promo-toggle-${p.id}`} />
                  <Badge className={`rounded-full border-0 ${p.activa ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-500"}`}>{p.activa ? "Activa" : "Inactiva"}</Badge>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{p.texto_para_el_agente}</p>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => { setForm({ ...empty, ...p }); setEditing(p.id); }} data-testid={`promo-editar-${p.id}`}>Editar</Button>
                <Button size="sm" variant="ghost" className="rounded-full text-red-500" onClick={() => del(p)} data-testid={`promo-borrar-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "Nueva promoción" : "Editar promoción"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="rounded-xl mt-1" data-testid="promo-nombre" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="porcentaje">Porcentaje</SelectItem><SelectItem value="monto_fijo">Monto fijo</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Valor</Label><Input type="number" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} className="rounded-xl mt-1" data-testid="promo-valor" /></div>
            </div>
            <div>
              <Label>Aplica a</Label>
              <Select value={form.aplica_a} onValueChange={(v) => setForm({ ...form, aplica_a: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="promo-aplica-a"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Toda la tienda</SelectItem>
                  <SelectItem value="marca">Marca</SelectItem>
                  <SelectItem value="categoria">Categoría</SelectItem>
                  <SelectItem value="productos">Productos específicos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.aplica_a === "productos" && (
              <div>
                <Label className="mb-1 block">Productos</Label>
                <ProductPicker value={null} onChange={addProducto} placeholder="Agregar producto…" testid="promo-product-picker" />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.productos_ids.map((pid) => (
                    <span key={pid} className="text-xs bg-accent rounded-full pl-2.5 pr-1 py-1 flex items-center gap-1" data-testid={`promo-chip-producto-${pid}`}>
                      {productosNombres[pid] || pid}
                      <button onClick={() => removeProducto(pid)}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {form.aplica_a === "marca" && (
              <div>
                <Label className="mb-1 block">Marcas</Label>
                <div className="flex flex-wrap gap-1.5">
                  {marcasDisponibles.length === 0 && <p className="text-xs text-muted-foreground">No hay marcas cargadas en productos todavía.</p>}
                  {marcasDisponibles.map((m) => (
                    <button key={m} type="button" onClick={() => toggleMarca(m)} data-testid={`promo-marca-chip-${m}`}
                      className={`text-xs rounded-full px-3 py-1.5 border ${form.marcas.includes(m) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {form.aplica_a === "categoria" && (
              <div>
                <Label className="mb-1 block">Categorías</Label>
                <div className="flex flex-wrap gap-1.5">
                  {categoriasDisponibles.map((c) => (
                    <button key={c} type="button" onClick={() => toggleCategoria(c)} data-testid={`promo-categoria-chip-${c}`}
                      className={`text-xs rounded-full px-3 py-1.5 border ${form.categorias.includes(c) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {preview && (
              <p className="text-xs text-muted-foreground" data-testid="promo-preview-alcance">
                Se aplica a {preview.total} producto{preview.total === 1 ? "" : "s"}.{" "}
                {preview.total > 0 && <button className="text-primary underline" onClick={() => setVerCuales(true)} data-testid="promo-ver-cuales">ver cuáles</button>}
              </p>
            )}

            <div><Label>Cómo debe ofrecerla el agente</Label><Textarea value={form.texto_para_el_agente} onChange={(e) => setForm({ ...form, texto_para_el_agente: e.target.value })} className="rounded-xl mt-1" /></div>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2"><span className="text-sm">Activa</span><Switch checked={form.activa} onCheckedChange={(v) => setForm({ ...form, activa: v })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} className="rounded-full bg-primary hover:bg-primary/90" data-testid="promo-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={verCuales} onOpenChange={setVerCuales}>
        <DialogContent className="max-w-sm max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Productos incluidos ({preview?.total})</DialogTitle></DialogHeader>
          <div className="space-y-1">
            {(preview?.items || []).map((p) => <p key={p.id} className="text-sm py-1 border-b border-border/60">{p.nombre}</p>)}
            {preview?.total > (preview?.items || []).length && <p className="text-xs text-muted-foreground pt-2">…y {preview.total - preview.items.length} más.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
